import { Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { toast } from "sonner";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentSourcePicker } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentSourcePicker";
import { PickedFileRow } from "web_myplann/Portfolio/Components/Redesign/Documents/PickedFileRow";
import {
	MAX_UPLOAD_SIZE_MB,
	type PickedFile,
} from "web_myplann/Portfolio/Forms/DocumentUploadForm/documentUploadFormSchema";
import { FlatRowSectionTitle } from "web_myplann/Shared/Components/FlatRow/FlatRowSectionTitle";
import type { AttachmentPicker } from "web_myplann/Shared/Types/AttachmentPicker";
import { runAttachmentPicker } from "web_myplann/Shared/Utils/runAttachmentPicker";
import { AttachmentPickStatuses } from "web_myplann/Utils/WebViewModel";

const R = Resources.Portfolio.List.DocumentUpload;

type Props = {
	value: PickedFile | null;
	onChange: (value: PickedFile | null) => void;
	disabled: boolean;
	errorMessage?: string;
};

export const DocumentFileField: FC<Props> = ({
	value,
	onChange,
	disabled,
	errorMessage,
}) => {
	const { palette, spacingTokens } = useTheme();
	const { t } = useResource();

	const handlePick = async (picker: AttachmentPicker) => {
		const picked = await runAttachmentPicker(picker);

		if (picked.status === AttachmentPickStatuses.cancelled) {
			return;
		}

		if (picked.status === AttachmentPickStatuses.error) {
			toast.error(t(R.pickError));
			return;
		}

		onChange({ file: picked.file, filename: picked.filename });
	};

	const handleRemove = () => onChange(null);

	return (
		<>
			<FlatRowSectionTitle title={t(R.fileSection)} />
			{value ? (
				<PickedFileRow
					file={value.file}
					filename={value.filename}
					onRemove={handleRemove}
				/>
			) : (
				<DocumentSourcePicker disabled={disabled} onPick={handlePick} />
			)}
			<Typography
				component="p"
				variant="caption"
				color={errorMessage ? palette.error.main : palette.grey[500]}
				px={spacingTokens.lg}
				pt={spacingTokens.xs}
			>
				{errorMessage ?? t(R.fileSizeHint, { maxSize: MAX_UPLOAD_SIZE_MB })}
			</Typography>
		</>
	);
};
